/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import axios from 'axios';

/**
 * @abstract Centralized tracking logic for Qubit events with queue behavior.
 * Queue is needed because there is a dependency on GA being ready from Tealium,
 * which loads slower than the tracking calls in render statements.
 */
class QubitEventTracker {
    constructor() {
        this.queue = [];
        this.gaId = null;
        this.interval = null;
        this.retryCount = 0;

        this.startPolling();
    }

    // only method to be directly when instantiating new class
    trackEvent = (trackingData) => {
        // GA ready and no events queued
        if (this.gaId && !this.queue.length) {
            const callbackUrl = this.prepTrackingCall(trackingData);
            this.callAxios(callbackUrl);
            // GA ready and events are queued
        } else if (this.gaId && this.queue.length) {
            const lastEventToBeQueued = this.prepTrackingCall(trackingData);
            this.queue.push(lastEventToBeQueued);
            this.clearQueuedEvents();
            // GA not ready
        } else {
            this.queueEvents(trackingData);
        }
    };

    // Add Qubit specific variables
    prepTrackingCall = ({ callbackData, eventType, partNumber }) => {
        let callbackUrl;
        let p;
        if (partNumber) {
            // if multiple partNumbers, seperate into indidivual p params
            if (partNumber.constructor === Array) {
                partNumber.map((number) => {
                    // first partNumber needs to have slightly different formatting to fit into callbackUrl structure
                    if (!p) {
                        p = `${number}`;
                        return p;
                    }
                    p = `${p}&p=${number}`;
                    return p;
                });
            } else {
                p = partNumber.toString();
            }
        }

        const baseUrl = 'https://api.qubit.com/placements/cb';
        const d = callbackData;
        const t = eventType;
        const ts = Date.now();
        // if GA is not ready add snippet to be replaced by this.insertGAId
        const gcid = this.gaId || '_GAID_';
        if (p) {
            callbackUrl = `${baseUrl}?d=${d}&t=${t}&p=${p}&ts=${ts}&gcid=${gcid}`;
        } else {
            callbackUrl = `${baseUrl}?d=${d}&t=${t}&ts=${ts}&gcid=${gcid}`;
        }

        return callbackUrl;
    };

    startPolling = () => {
        if (typeof window === 'undefined') {
            return;
        }
        this.interval = setInterval(this.checkGAReady, 5000);
    };

    checkGAReady = () => {
        const retries = this.retryCount;

        if (window?.ga?.loaded) {
            this.gaId = window.ga.getAll()[0].get('clientId');
            clearInterval(this.interval);
            this.interval = null;
            this.clearQueuedEvents();
        } else if (retries > 100) {
            clearInterval(this.interval);
            this.interval = null;
            this.clearQueuedEvents();
        }

        this.retryCount = retries + 1;
    };

    insertGAId = (event) => event.replace('_GAID_', this.gaId);

    queueEvents = (trackingData) => {
        const queue = this.queue;
        const eventWithoutGA = this.prepTrackingCall(trackingData);
        queue.push(eventWithoutGA);
        this.queue = queue;
    };

    clearQueuedEvents = () => {
        const queue = this.queue;
        this.queue = [];

        queue.forEach((event) => {
            const eventWithGA = this.insertGAId(event);
            this.callAxios(eventWithGA);
        });
    };

    callAxios = (url) => {
        try {
            axios.get(url);
        } catch (ex) {
            if (typeof window !== 'undefined') window.toastr.error('qubitTrackEvent FAILED');
        }
    };
}

const EventTracker = new QubitEventTracker();

export default EventTracker;
